import { cloneDeep, each, filter, get, isEmpty, isNil, set } from "lodash";
import ReactGA from "react-ga4";
import comments_operations from "../../comments/ducks/operations";
import compOperations from "../../comp/ducks/operations";
import documentConstants from "../../documents/constants";
import documentOperations from "../../documents/ducks/operations";
import listingConstants from "../../listing/constants";
import listingActions from "../../listing/ducks/actions";
import listingOperations from "../../listing/ducks/operations";
import listingValidation from "../../listing/ducks/validation";
import searchActions from "../../search/ducks/actions";
import { request } from "../../utils/axios-wrapper";
import { pageSize, us_states_options } from "../../utils/constants";
import utils from "../../utils/index";
import utilOperations, { convertForIntegerValidation, convertForNumberValidation } from "../../utils/operations";
import constants from "../constants";
import actions from "./actions";
import validation from "./validation";

const validateForm = (dispatch, validationResponse) => {
    if (validationResponse) {
        each(Object.keys(validationResponse), (key) => {
            dispatch(utils.actions.addError(key, validationResponse[key][0]));
        });
    }
    return validationResponse;
};

const togglePropertySearchModal = (isOpen) => {
    return (dispatch, getState) => {
        dispatch(listingActions.clearErrors());
        dispatch(actions.togglePropertySearchModal(isOpen));
    };
};

const toggleUpdateDialog = (isOpen) => {
    return (dispatch, getState) => {
        if (isOpen === true) {
            const validationResponse = validateForm(
                dispatch,
                listingValidation.validateRequestListingFromExisting(
                    get(getState().listingReducer, "requestModal.selectedProperty")
                )
            );
            if (validationResponse) {
                dispatch(utils.actions.loading(false));
            } else {
                dispatch(actions.updatePropertyForListingDialog(isOpen));
            }
        } else {
            dispatch(actions.updatePropertyForListingDialog(isOpen));
        }
    };
};

const togglePropertyRequestModal = () => {
    return (dispatch, getState) => {
        dispatch(actions.togglePropertyRequestModal(!getState().propertyReducer.propertyRequestModal.open));
    };
};

const updatePropertyRequest = (field, value) => {
    return (dispatch, getState) => {
        dispatch(actions.updatePropertyRequest(field, value));
    };
};

const displayDeleteDialog = (isOpen, propertyKey) => {
    return (dispatch, getState) => {
        request({
            url: listingConstants.urls.unassigned_listings_by_property(propertyKey),
            method: "get",
            crossDomain: true,
        })
            .then((response) => {
                if (!isEmpty(response.data)) {
                    dispatch(
                        utils.actions.snackbar(
                            true,
                            "error",
                            "You cannot delete a Property Request that is associated with a Listing, Listing Request, Deal, or Outside Comp. Please move any associated data to a new Property before trying to delete this Property Request."
                        )
                    );
                } else {
                    dispatch(actions.updateDeletePropertyDialog(isOpen));
                }
            })
            .catch((err) => {
                console.log(err);
            });
    };
};

const handleDeleteProperty = (propertyKey) => {
    return (dispatch, getState) => {
        dispatch(utils.actions.clearErrors());
        dispatch(utils.actions.somethingSaving(true, "propertyDelete"));
        dispatch(actions.updateDeletePropertyDialog(false));
        return request(
            {
                method: "delete",
                url: constants.urls.delete_property(propertyKey),
            },
            dispatch
        )
            .then((response) => {
                dispatch(utils.actions.somethingSaving(false, "propertyDelete"));
                dispatch(utils.actions.snackbar(true, "success", "Property Request Deleted"));
                return { success: true };
            })
            .catch((err) => {
                dispatch(utils.actions.somethingSaving(false, "propertyDelete"));
                dispatch(
                    utils.actions.snackbar(
                        true,
                        "error",
                        "Unable to delete this Property Request because it is associated with a Listing, Deal, or Outside Comp."
                    )
                );
                console.log(err);
                dispatch(
                    utilOperations.reportError(
                        err,
                        "listing:operations:handleDeletePropertyRequest - Error trying to delete property request"
                    )
                );
                return { success: false };
            });
    };
};

const requestProperty = () => {
    return (dispatch, getState) => {
        const state = getState().propertyReducer;
        dispatch(utils.actions.clearErrors());
        const validationResponse = validateForm(
            dispatch,
            validation.validateRequestProperty(get(state, "propertyRequestModal"))
        );
        if (validationResponse) {
            return false;
        } else {
            request(
                {
                    url: constants.urls.request_property(),
                    method: "post",
                    data: buildPropertyRequestFromState(state),
                    headers: {
                        "content-type": "application/json",
                    },
                },
                dispatch
            )
                .then((response) => {
                    ReactGA.event("Property Request Submitted", {
                        propertyName: state.propertyRequestModal.propertyName,
                    });
                    dispatch(togglePropertyRequestModal());
                    dispatch(utils.actions.snackbar(true, "success", "Property request submitted"));
                })
                .catch((err) => {
                    console.log(err);
                    dispatch(
                        utils.actions.snackbar(true, "error", "Error trying to submit a property request \n " + err)
                    );
                    dispatch(utilOperations.reportError(err, "property:operations:requestProperty Error"));
                });
        }
    };
};

const buildPropertyRequestFromState = (state) => {
    const modalData = state.propertyRequestModal;
    let property = {};

    set(property, "propertyAttributes.propertyName", modalData.propertyName);
    set(property, "propertyType", modalData.propertyType);
    set(property, "primaryAddress.address1", modalData.propertyAddress);
    set(property, "primaryAddress.city.city", modalData.propertyCity);
    set(property, "primaryAddress.city.state.stateCode", modalData.propertyState);
    set(
        property,
        "primaryAddress.city.state.stateName",
        filter(us_states_options, (x) => x.abbreviation === modalData.propertyState)[0]["label"]
    );
    set(property, "primaryAddress.zipCode.zipCode", modalData.propertyZip);
    set(property, "memberComments", modalData.memberComments);
    set(property, "publicComments", modalData.publicComments);

    return property;
};

const buildPropertyRequest = (data) => {
    const fieldsToConvertToDouble = [
        "propertyAttributes.totalAcres",
        "propertyAttributes.totalBldgNra",
        "propertyAttributes.totalBldgSf",
        "propertyAttributes.totalLandSf",
        "propertyAttributes.percentLeased",
        "propertyAttributes.lotAcres",
        "propertyAttributes.hospitalitySf",
    ];

    const fieldsToConvertToInteger = [
        "propertyAttributes.parkingTotal",
        "propertyAttributes.elevators",
        "propertyAttributes.numBldg",
        "propertyAttributes.freightElevatorCount",
        "propertyAttributes.parkingGarageSpaceCount",
        "propertyAttributes.parkingReservedSpaceCount",
        "propertyAttributes.surfaceParkingSpaceCount",
        "propertyAttributes.carportParkingSpaceCount",
        "propertyAttributes.lotSf",
        "propertyAttributes.numFloors",
        "propertyAttributes.hospitalityUnitCount",
        "propertyAttributes.roomCount",
    ];

    let property = {
        ...data,
    };

    each(fieldsToConvertToDouble, (field) => {
        set(property, field, convertForNumberValidation(get(property, field)));
    });

    each(fieldsToConvertToInteger, (field) => {
        set(property, field, convertForIntegerValidation(get(property, field)));
    });

    return property;
};

const getPropertyDetail = (key, loadImages) => {
    return (dispatch, _) => {
        dispatch(getProperty(key, loadImages));
        dispatch(listingOperations.getOpenListingsByProperty(key, "property_listings"));
        dispatch(compOperations.getCompHistoryByPropertyKey(key));
        dispatch(getPropertyManagementDetails(key));
        dispatch(getPropertyManagementHistory(key));
    };
};

const getProperty = (key, loadImages) => {
    return (dispatch, _) => {
        dispatch(utils.actions.loading(true));
        return request(
            {
                method: "get",
                url: constants.urls.get_property(key),
            },
            dispatch
        )
            .then((response) => {
                dispatch(loadProperty(response.data));
                if (loadImages === true) {
                    dispatch(getPropertyImages(key));
                }

                dispatch(utils.actions.loading(false));
            })
            .catch((err) => {
                dispatch(loadProperty({}));
                dispatch(utils.actions.snackbar(true, "error", "Error trying to retrieve property: " + err));
                dispatch(utils.actions.loading(false));
                dispatch(utilOperations.reportError(err, "Error trying to retrieve property"));
                return true;
            });
    };
};

const getPropertyImages = (key) => {
    return (dispatch, _) => {
        dispatch(utils.actions.loading(true));
        request(
            {
                url: constants.urls.get_property_images(key),
                method: "get",
            },
            dispatch
        )
            .then((response) => {
                dispatch(loadPropertyImages(response.data));
                dispatch(utils.actions.loading(false));
            })
            .catch((err) => {
                dispatch(loadPropertyImages({}));
                dispatch(utils.actions.snackbar(true, "error", "Error trying to retrieve property images: " + err));
                dispatch(utils.actions.loading(false));
                dispatch(utilOperations.reportError(err, "Error trying to retrieve property images"));
            });
    };
};

const loadProperty = (property) => {
    return (dispatch, _) => {
        dispatch(actions.loadProperty(property));
    };
};

const loadPropertyImages = (images) => {
    return (dispatch, _) => {
        dispatch(actions.loadPropertyImages(images));
    };
};

const handleInputChange = (field, value) => {
    return (dispatch, getState) => {
        dispatch(actions.inputChange(field, value));
    };
};

const addArrayElement = (arrayPath) => {
    return (dispatch, getState) => {
        dispatch(actions.addArrayElement(arrayPath));
    };
};

const addArrayElement_v2 = (pathToArray, obj = {}) => {
    return (dispatch, getState) => {
        let array = get(getState().propertyReducer, pathToArray, []);
        if (array == null) {
            array = [];
        }
        array.push(obj);
        dispatch(actions.setPropertyArray(pathToArray, array));
    };
};

const deleteArrayElement = (arrayPath, index) => {
    return (dispatch, getState) => {
        dispatch(actions.deleteArrayElement(arrayPath, index));
    };
};
const deleteArrayElement_v2 = (pathToArray, index) => {
    return (dispatch, getState) => {
        let array = cloneDeep(get(getState().propertyReducer, pathToArray, []));
        array.splice(index, 1);
        dispatch(actions.setPropertyArray(pathToArray, array));
    };
};

const switchTab = (tab) => {
    return (dispatch, getState) => {
        dispatch(actions.switchTab(tab));
        if (tab === "all_properties") {
            dispatch(searchActions.onSearchPage(true));
        } else {
            dispatch(searchActions.onSearchPage(false));
        }
    };
};

const switchPropDetailTab = (tab) => {
    return (dispatch, getState) => {
        ReactGA.event("Property Detail Tab Change", {
            tab: tab,
        });
        dispatch(actions.switchPropDetailTab(tab));
    };
};

const getProperties = () => {
    return (dispatch, getState) => {
        const { needsReviewPage, needsReviewSort, needsReviewFilter } = getState().propertyReducer;
        const tab = "data_team_needs_review";
        dispatch(utils.actions.somethingSaving(true, tab));
        const params = new URLSearchParams({
            page: needsReviewPage - 1,
            size: pageSize,
        });
        if (needsReviewSort) {
            params.append("sort", needsReviewSort);
        }
        params.append("sort", "id,desc");
        if (
            needsReviewFilter?.field &&
            (needsReviewFilter?.value || (needsReviewFilter?.dateFrom && needsReviewFilter?.dateTo))
        ) {
            params.append("filterColumn", needsReviewFilter.field);
            if (
                needsReviewFilter.field === "auditLastModifiedTimestamp" &&
                needsReviewFilter.dateFrom &&
                needsReviewFilter.dateTo
            ) {
                params.append("dateFrom", needsReviewFilter.dateFrom);
                params.append("dateTo", needsReviewFilter.dateTo);
            } else if (needsReviewFilter.value) {
                params.append("filterValue", needsReviewFilter.value);
            }
        }

        dispatch(utils.actions.loading(true));
        request(
            {
                url: `${constants.urls[tab](getState().authReducer.userProfile.user)}?${params}`,
                method: "get",
            },
            dispatch
        )
            .then((response) => {
                dispatch(actions.receiveProperties(response.data, tab));
                dispatch(utils.actions.somethingSaving(false, tab));
                dispatch(utils.actions.loading(false));
            })
            .catch((err) => {
                dispatch(utils.actions.somethingSaving(false, tab));
                dispatch(utils.actions.loading(false));
                dispatch(utilOperations.reportError(err, "Error trying to retrieve Properties by status"));
            });
    };
};

const addPropertyManagementSection = (key) => {
    return (dispatch, getState) => {
        request(
            {
                url: constants.urls.create_property_management(key),
                method: "post",
            },
            dispatch
        )
            .then((response) => {
                dispatch(actions.inputChange("propertyManagement", response.data));
                dispatch(getPropertyManagementPersonnel(response.data.propertyManagementKey));
            })
            .catch((err) => {
                dispatch(utils.actions.snackbar(true, "error", "Error trying to add property management: " + err));
                dispatch(utilOperations.reportError(err, "property:operations:addPropertyManagementSectino Error"));
            });
    };
};

const getPropertyManagementDetails = (key) => {
    return (dispatch, getState) => {
        dispatch(utils.actions.somethingSaving(true, "property_management_details"));
        dispatch(utils.actions.loading(true));
        request(
            {
                url: constants.urls.property_management_details(key),
                method: "get",
            },
            dispatch
        )
            .then((response) => {
                dispatch(actions.inputChange("propertyManagement", response.data));
                if (get(response, "data.propertyManagementKey", null)) {
                    dispatch(getPropertyManagementPersonnel(response.data.propertyManagementKey));
                }
                dispatch(utils.actions.loading(false));
                dispatch(utils.actions.somethingSaving(false, "property_management_details"));
            })
            .catch((err) => {
                dispatch(
                    utils.actions.snackbar(
                        true,
                        "error",
                        "Error trying to retrieve property management details: " + err
                    )
                );
                dispatch(utils.actions.loading(false));
                dispatch(utils.actions.somethingSaving(false, "property_management_details"));
                dispatch(utilOperations.reportError(err, "property:operations:getPropertyManagementDetails Error"));
            });
    };
};

const getPropertyManagementHistory = (key) => {
    return (dispatch, getState) => {
        dispatch(utils.actions.somethingSaving(true, "property_management_history"));
        dispatch(utils.actions.loading(true));
        request(
            {
                url: constants.urls.property_management_history(key),
                method: "get",
            },
            dispatch
        )
            .then((response) => {
                dispatch(actions.inputChange("propertyManagementSnapshots", response.data));
                dispatch(utils.actions.loading(false));
                dispatch(utils.actions.somethingSaving(false, "property_management_history"));
            })
            .catch((err) => {
                dispatch(
                    utils.actions.snackbar(
                        true,
                        "error",
                        "Error trying to retrieve property management history: " + err
                    )
                );
                dispatch(utils.actions.loading(false));
                dispatch(utils.actions.somethingSaving(false, "property_management_history"));
                dispatch(utilOperations.reportError(err, "property:operations:getPropertyManagementHistory Error"));
            });
    };
};

const getPropertyManagementPersonnel = (key) => {
    return (dispatch, getState) => {
        dispatch(utils.actions.somethingSaving(true, "property_management_personnel"));
        dispatch(utils.actions.loading(true));
        request(
            {
                url: constants.urls.property_management_personnel(key),
                method: "get",
            },
            dispatch
        )
            .then((response) => {
                dispatch(actions.inputChange("propertyManagement.personnel", response.data));
                dispatch(actions.inputChange("propertyManagement.personnelInfo", Object.values(response.data).flat()));
                dispatch(utils.actions.loading(false));
                dispatch(utils.actions.somethingSaving(false, "property_management_personnel"));
            })
            .catch((err) => {
                dispatch(utils.actions.snackbar(true, "error", "Error trying to retrieve comps: " + err));
                dispatch(utils.actions.loading(false));
                dispatch(utils.actions.somethingSaving(false, "property_management_personnel"));
                dispatch(utilOperations.reportError(err, "property:operations:getPropertyManagementPersonnel Error"));
            });
    };
};

const savePropertyManagementDetails = (key, sync = false) => {
    return (dispatch, getState) => {
        dispatch(utils.actions.loading(true));
        dispatch(utils.actions.clearErrors());

        const data = buildPropertyManagementRequest(getState());
        const errors = validateForm(dispatch, validation.validatePropertyManagementDetails(data.propertyManagement));
        const snackbarMessage = "Errors found - please see red highlighted fields.";
        if (errors) {
            dispatch(utils.actions.snackbar(true, "error", snackbarMessage));
            dispatch(utils.actions.addError("property", errors));
            return false;
        } else {
            request(
                {
                    method: "put",
                    url: constants.urls.update_property_management_details(key),
                    data: data,
                    params: { sync },
                    headers: { "Content-Type": "application/json" },
                },
                dispatch
            )
                .then((response) => {
                    dispatch(actions.inputChange("propertyManagement", response.data));
                    dispatch(actions.inputChange("propertyManagement.personnel", response.data.personnel));
                    dispatch(
                        actions.inputChange(
                            "propertyManagement.personnelInfo",
                            Object.values(response.data.personnel).flat()
                        )
                    );
                    dispatch(utils.actions.loading(false));
                    dispatch(utils.actions.snackbar(true, "success", "All changes saved"));
                })
                .catch((err) => {
                    dispatch(
                        utils.actions.snackbar(
                            true,
                            "error",
                            "You have input the wrong datatype in a numeric or date field."
                        )
                    );
                    dispatch(utils.actions.loading(false));
                    dispatch(
                        utilOperations.reportError(err, "property:operations:savePropertyManagementDetails Error")
                    );
                });
        }
    };
};

const handleSubmitPropertyForm = (save, property, dispatch) => {
    if (save) {
        const data = buildPropertyRequest(property);
        return request(
            {
                method: "put",
                url: constants.urls.save_property(property.propertyKey),
                data: data,
                headers: { "Content-Type": "application/json" },
            },
            dispatch
        );
    }
};

const handleSave = () => {
    return handleSaveCommon("propertySaveAndContinue");
};

const handleSaveAndExit = () => {
    return handleSaveCommon("propertySaveAndExit");
};

const handleSaveCommon = (elementSaving) => {
    return (dispatch, getState) => {
        dispatch(utils.actions.clearErrors());
        dispatch(utils.actions.somethingSaving(true, elementSaving));
        const property = getState().propertyReducer.property;
        const errors = validation.validateProperty(property, get(property, "propertyType"), "Save");
        if (isEmpty(errors) || isNil(errors)) {
            return handleSubmitPropertyForm(true, property, dispatch)
                .then((response) => {
                    dispatch(utils.actions.somethingSaving(false, elementSaving));
                    dispatch(utils.actions.snackbar(true, "success", "Success."));
                    return { success: true };
                })
                .catch((err) => {
                    dispatch(utils.actions.snackbar(true, "error", "Error trying to save: " + err));
                    dispatch(utilOperations.reportError(err, "property:operations:handleSaveAndExit Error"));
                    dispatch(utils.actions.somethingSaving(false, elementSaving));
                    return { success: false };
                });
        } else {
            const errorMessage =
                "A required field is missing or the wrong datatype has been input in a field. " +
                "Please see the red highlighted fields and correct these issues. ";
            dispatch(utils.actions.snackbar(true, "error", errorMessage));
            dispatch(utils.actions.addError("property", errors));
            dispatch(utils.actions.somethingSaving(false, elementSaving));
            return { success: false };
        }
    };
};

const handleApprovePropertyRequestAsDataEntry = () => {
    return (dispatch, getState) => {
        dispatch(utils.actions.clearErrors());
        dispatch(utils.actions.somethingSaving(true, "propertySubmit"));
        const property = getState().propertyReducer.property;
        const documentState = getState().documentsReducer;
        const errors = validation.validateProperty(property, get(property, "propertyType"), "Approve", documentState);
        if (isEmpty(errors) || isNil(errors)) {
            return handleSubmitPropertyForm(true, property, dispatch)
                .then((response) => {
                    return request(
                        {
                            method: "put",
                            url: constants.urls.update_property_status(property.propertyKey),
                            data: {
                                propertyStatus: "Active",
                            },
                            headers: { "Content-Type": "application/json" },
                        },
                        dispatch
                    ).then((response) => {
                        dispatch(utils.actions.somethingSaving(false, "propertySubmit"));
                        return { success: true };
                    });
                })
                .catch((err) => {
                    dispatch(utils.actions.somethingSaving(false, "propertySubmit"));
                    dispatch(utils.actions.snackbar(true, "error", "Error trying to save: " + err));
                    dispatch(
                        utilOperations.reportError(
                            err,
                            "property:operations:handleApprovePropertyRequestAsDataEntry Error"
                        )
                    );
                    return { success: false };
                });
        } else {
            let errorMessage = "";
            const hasErrors = Object.keys(errors).length > 1;
            const hasMissingRequiredDocuments = Object.keys(errors).includes("documents");
            if (hasErrors && hasMissingRequiredDocuments) {
                errorMessage =
                    "A required field is missing or the wrong datatype has been input in a field. " +
                    "Please see the red highlighted fields and correct these issues. " +
                    "There are missing required documents, please ensure these are attached.";
            } else if (hasMissingRequiredDocuments) {
                errorMessage = "There are missing required documents, please ensure these are attached.";
                // Catchall, the only errors found are input field errors if any of the above conditions are not met
            } else {
                errorMessage =
                    "A required field is missing or the wrong datatype has been input in a field. " +
                    "Please see the red highlighted fields and correct these issues.";
            }
            dispatch(utils.actions.snackbar(true, "error", errorMessage));
            dispatch(utils.actions.addError("property", errors));
            dispatch(utils.actions.somethingSaving(false, "propertySubmit"));
            return { success: false };
        }
    };
};

const clearPropertyDetailState = () => {
    return (dispatch) => {
        dispatch(actions.resetState());
    };
};

const navigateToRequestPropertyUpdate = (key, redirectTo) => {
    return (dispatch) => {
        dispatch(comments_operations.setStateForRequestUpdate(redirectTo));
    };
};

const archiveAssetServicesRecord = (propertyManagementKey, record, propertyKey) => {
    return (dispatch, getState) => {
        dispatch(utils.actions.clearErrors());
        dispatch(utils.actions.somethingSaving(true, "asset_services_record"));
        const errors = validation.validateArchiveAssetRecord(record);

        if (isEmpty(errors) || isNil(errors)) {
            return request(
                {
                    method: "post",
                    url: constants.urls.archive_asset_services_record(propertyManagementKey),
                    data: record,
                    headers: { "Content-Type": "application/json" },
                    params: record,
                },
                dispatch
            )
                .then((response) => {
                    dispatch(utils.actions.snackbar(true, "success", "Archive Snapshot saved"));
                    dispatch(utils.actions.somethingSaving(false, "asset_services_record"));
                    dispatch(getPropertyManagementDetails(propertyKey));
                    dispatch(getPropertyManagementHistory(propertyKey));
                    dispatch(
                        documentOperations.getDocuments(
                            documentConstants.urls.get_documents(propertyManagementKey, "PROPERTY_MANAGEMENT")
                        )
                    );
                    return { success: true };
                })
                .catch((err) => {
                    dispatch(utils.actions.snackbar(true, "error", "Error trying to save: " + err));
                    dispatch(utils.actions.somethingSaving(false, "asset_services_record"));
                    dispatch(utilOperations.reportError(err, "property:operations:archiveAssetServicesRecord Error"));
                    return { success: false };
                });
        } else {
            dispatch(utils.actions.addError("archiveAssetServices", errors));
            return { success: false };
        }
    };
};

const deleteAssetServicesRecord = (propertyManagementKey) => {
    return (dispatch, getState) => {
        dispatch(utils.actions.loading(true));
        request({
            url: constants.urls.delete_asset_services_record(propertyManagementKey),
            method: "delete",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => {
                dispatch(actions.inputChange("propertyManagement", response.data));
                dispatch(utils.actions.snackbar(true, "success", "Asset Services deleted."));
                dispatch(utils.actions.loading(false));
            })
            .catch((err) => {
                dispatch(utils.actions.snackbar(true, "error", "Unable to delete Asset Services. Please try again."));
                dispatch(utils.actions.loading(false));
            });
    };
};

const setNeedsReviewPage = (page) => {
    return (dispatch) => {
        dispatch(actions.setNeedsReviewPage(page));
    };
};

const setNeedsReviewSort = (sort) => {
    return (dispatch) => {
        dispatch(actions.setNeedsReviewSort(sort));
    };
};

const setNeedsReviewFilter = (filter) => {
    return (dispatch) => {
        dispatch(actions.setNeedsReviewFilter(filter));
    };
};

const resetNeedsReviewTab = () => {
    return (dispatch) => {
        dispatch(actions.setNeedsReviewPage(1));
        dispatch(actions.setNeedsReviewSort("auditLastModifiedTimestamp,desc"));
        dispatch(actions.setNeedsReviewFilter(null));
    };
};

const buildPropertyManagementRequest = (state) => {
    const pmData = state.propertyReducer.propertyManagement;
    const personnelData = groupPersonnelByRole(pmData.personnelInfo);
    const pmandPersonnelData = { propertyManagement: pmData, personnel: personnelData };
    return pmandPersonnelData;
};

const groupPersonnelByRole = (updatedPersonnel) => {
    const groupedByRole = {};
    if (updatedPersonnel) {
        updatedPersonnel.forEach((personnelItem) => {
            if (personnelItem.person && personnelItem.role !== "Select") {
                if (!groupedByRole[personnelItem.role]) {
                    groupedByRole[personnelItem.role] = [];
                }
                groupedByRole[personnelItem.role].push(personnelItem);
            }
        });
        actions.inputChange("propertyManagement.personnel", groupedByRole);
    }
    return groupedByRole;
};

export default {
    togglePropertyRequestModal,
    togglePropertySearchModal,
    updatePropertyRequest,
    requestProperty,
    getProperty,
    getPropertyDetail,
    addArrayElement,
    addArrayElement_v2,
    deleteArrayElement,
    deleteArrayElement_v2,
    switchTab,
    getProperties,
    handleInputChange,
    loadProperty,
    switchPropDetailTab,
    addPropertyManagementSection,
    getPropertyManagementDetails,
    getPropertyManagementHistory,
    getPropertyManagementPersonnel,
    savePropertyManagementDetails,
    handleSubmitPropertyForm,
    handleApprovePropertyRequestAsDataEntry,
    handleSave,
    handleSaveAndExit,
    clearPropertyDetailState,
    navigateToRequestPropertyUpdate,
    buildPropertyRequest,
    displayDeleteDialog,
    handleDeleteProperty,
    toggleUpdateDialog,
    archiveAssetServicesRecord,
    deleteAssetServicesRecord,
    setNeedsReviewPage,
    setNeedsReviewSort,
    setNeedsReviewFilter,
    resetNeedsReviewTab,
};
